import React, { useState } from "react"
import { StaticImage } from "gatsby-plugin-image"
import { btnCva } from "../../../ui/button"
import Button from "../../button"
import LayoutAlignLeft from "../../icons/layout-align-left"
import SlideIcon from "../../icons/slide-icon"
import FileTextIcon from "../../icons/file-text-icon"
import GraduateCapIcon from "../../icons/graduate-cap-icon"
import ToolboxIcon from "../../icons/toolbox-icon"
import cn from "../../../utils/classname"

type TAB = {
  id: number
  name: string
  Icon: React.ElementType
}
type ImageProps = {
  tabId: number
  isActive: boolean
}

const TABS: TAB[] = [
  { id: 1, name: "Dashboard", Icon: LayoutAlignLeft },
  { id: 2, name: "Courses", Icon: SlideIcon },
  { id: 3, name: "Articles", Icon: FileTextIcon },
  { id: 4, name: "Learning Path", Icon: GraduateCapIcon },
  { id: 5, name: "Toolbox", Icon: ToolboxIcon },
]

const TabImage = ({ tabId, isActive }: ImageProps) => {
  if (!isActive) return null

  const imageProps: any = {
    className: "rounded-2xl border-4 border-neutral-5",
    quality: 100,
    loading: "eager",
  }

  return (
    <div className="relative">
      {tabId === 1 && (
        <StaticImage
          src="../../../images/developer-info/dashboard-hq.png"
          alt="Dashboard overview"
          {...imageProps}
        />
      )}
      {tabId === 2 && (
        <StaticImage
          src="../../../images/developer-info/courses-hq.png"
          alt="Courses overview"
          {...imageProps}
        />
      )}
      {tabId === 3 && (
        <StaticImage
          src="../../../images/developer-info/articles-hq.png"
          alt="Articles overview"
          {...imageProps}
        />
      )}
      {tabId === 4 && (
        <StaticImage
          src="../../../images/developer-info/learning-path-hq.png"
          alt="Learning Path overview"
          {...imageProps}
        />
      )}
      {tabId === 5 && (
        <StaticImage
          src="../../../images/developer-info/toolbox-hq.png"
          alt="Toolbox overview"
          {...imageProps}
        />
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white to-transparent rounded-b-2xl pointer-events-none" />
    </div>
  )
}

const DeveloperInfo = () => {
  const [activeTab, setActiveTab] = useState<number>(1)

  return (
    <div className="container-lg mt-[100px] sm:mt-[120px]">
      <div className="flex flex-col sm:items-center max-w-[680px] mx-auto">
        <div className="text-left sm:text-center">
          <p className="text-jar-3 font-medium italic text-neutral-11">
            The home for developers
          </p>
          <div className="text-jar-8 text-neutral-12 font-semibold leading-9 tracking-tight">
            Track your course progress, manage projects, and find your dream job
            - all in one place.
          </div>
        </div>
        <a
          href="/premium/pricing/"
          className={cn(
            btnCva({
              size: 4,
              variant: "solid",
              theme: "primary",
            }),
            "mt-8 w-ful sm:w-fit"
          )}
        >
          Start your free Trial
        </a>
      </div>
      <div className="relative sm:hidden flex justify-center mt-8">
        <StaticImage
          src="../../../images/developer-info/mobile-screen-hq.png"
          alt="Toolbox overview"
          objectFit="cover"
          quality={100}
          loading="eager"
          class="rounded-2xl border-4 border-neutral-5"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white to-transparent rounded-b-2xl pointer-events-none" />
      </div>

      <div className="hidden sm:flex flex-col gap-6 mt-16">
        <div className="flex flex-row gap-2 justify-center">
          {TABS.map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? "soft" : "ghost"}
              className={cn(
                "h-8 px-2 text-jar-3 font-medium",
                activeTab === tab.id ? " text-neutral-12" : "text-neutral-11"
              )}
            >
              <tab.Icon className="mr-1" />
              {tab.name}
            </Button>
          ))}
        </div>
        <div>
          {TABS.map((tab) => (
            <TabImage
              key={tab.id}
              tabId={tab.id}
              isActive={activeTab === tab.id}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default DeveloperInfo
